import { useEffect, useState } from "react";
import { Box, Grid } from "@mui/material";
import GadgetCard from "./GadgetCard";
import { useQuery } from "react-query";
import { GadgetSortColumns } from "./GadgetModel";
import { GadgetDaoFactory, GadgetPaginatedResponse } from "./GadgetRepository";
import Pagination from "@mui/material/Pagination";
import Spinner from "../../../../components/Loader/Spinner";
import { GadgetPlaform } from "./GadgetPlaform";
import { NoGadgetFound } from "./NoGadgetFound";
import { GadgetCountLabel } from "./GadgetCountLabel";
import { notify } from "../../../../common/state/Slices/NotificationSlice";
import { useAppDispatch, useAppSelector } from "../../../../common/state/hooks";
import { KeycloakClientFactory } from "../../../../helper/keycloak/factory";
import { useLocation, useNavigate } from "react-router-dom";

interface GadgetsGridDisplayProps {
  plaform: GadgetPlaform;
  gadgetName?: string;
  sortModel: GadgetSortColumns;
  currentPage: number;
  setCurrentPage: (page: number) => void;
}

const GadgetsGridDisplay = ({
  plaform,
  gadgetName,
  sortModel,
  currentPage,
  setCurrentPage
}: GadgetsGridDisplayProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector((state) => state.auth.authenticated);
  const keycloak = KeycloakClientFactory.getInstance();
  const pageSize = 12;
  const dispatch = useAppDispatch();
  const [provider, setProvider] = useState<string>("");

  const getPublishedGadgets = (
    size: number,
    page: number,
    plaform: GadgetPlaform
  ) => {
    return GadgetDaoFactory.getGadgetRepository()
      .getAllPublished(page, size, sortModel, plaform, provider, gadgetName)
      .then((result) => result);
  };

  const { data, isLoading, isError } = useQuery<GadgetPaginatedResponse, Error>(
    [currentPage, plaform, gadgetName, sortModel, isAuthenticated, provider],
    () => getPublishedGadgets(pageSize, currentPage, plaform)
  );

  useEffect(() => {
    if(isAuthenticated) {
      const userData = keycloak.getIdToken();
      setProvider(userData.groups[0].name);
    }
  }, [isAuthenticated, keycloak]);

  const setQueryParams = (pageNo: number) => {
    const q = new URLSearchParams(location.search);
    q.set("page_no", pageNo.toString());
    navigate(location.pathname + "?" + q.toString());
  };

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setQueryParams(value - 1);
    setCurrentPage(value - 1);
  };

  if (isLoading) {
    return <Spinner />;
  }

  if(isError) {
    dispatch(
      notify({
        message: "Failed to fetch agents.",
        severity: "error"
      })
    );
  }

  if (data?.total_items === 0 || isError) {
    return (
      <NoGadgetFound dataTestId={`no-${plaform}-gadgets`} platform={plaform} />
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <GadgetCountLabel
        platform={plaform}
        count={data?.total_items || 0}
      />
      <Grid container spacing={4} data-testid={"gadgets-grid"}>
        {data?.data.map((item) => (
          <Grid item xs={6} md={3} lg={2} key={item.id}>
            <GadgetCard gadget={{ ...item, supportedOs: plaform }} />
          </Grid>
        ))}
      </Grid>
      <Box
        display="flex"
        justifyContent="center"
        mt={5}
        data-testid={"pagination-container"}
      >
        <Pagination
          page={currentPage + 1}
          count={data?.total_pages}
          variant="outlined"
          shape="rounded"
          onChange={handleChangePage}
        />
      </Box>
    </Box>
  );
};

export default GadgetsGridDisplay;
